import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, Chip, Collapse, IconButton, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import authFetch from '../utils/authFetch';
import { fmtQty, sourceRowLabel, clickableA11y } from '../styles/ds';

const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

/**
 * Actual supplier/batch fulfillment for a delivered order item — computed
 * from batch_allocations (what was really consumed), shown regardless of
 * whether the item used automatic FIFO or an explicit supplier plan.
 * Collapsed by default behind its own small "אספקה בפועל" toggle (clicking
 * the product row reveals it) — only the toggle itself renders eagerly
 * (once loaded and non-empty); the detail (qty/source/production batch
 * link) stays hidden until clicked.
 */
const SupplierFulfillmentPanel = ({ orderItemId, unit }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null); // null = loading
  const [openBatches, setOpenBatches] = useState(null); // which group's batch-date detail is expanded
  const [expanded, setExpanded] = useState(false); // the whole panel's own collapsed-by-default state

  const load = useCallback(() => {
    authFetch(`/order_items/${orderItemId}/fulfillment`)
      .then(r => r.json())
      .then(d => setGroups(Array.isArray(d) ? d : []))
      .catch(() => setGroups([]));
  }, [orderItemId]);

  useEffect(() => { load(); }, [load]);

  if (groups === null || groups.length === 0) return null;

  return (
    <Box sx={{ mt: 0.5, mb: 1, p: 1.2, backgroundColor: 'rgba(212,237,218,0.2)', borderRadius: 1.5, border: '1px solid rgba(21,87,36,0.15)' }}>
      <Stack direction="row" alignItems="center" spacing={0.5}
        onClick={() => setExpanded(e => !e)}
        {...clickableA11y(() => setExpanded(e => !e), { ariaLabel: 'סופק בפועל מ — הצג/הסתר', asButton: true })}
        aria-expanded={expanded}
        sx={{ cursor: 'pointer', userSelect: 'none', width: 'fit-content' }}>
        <Typography fontSize={11} fontWeight={700} color="#155724">סופק בפועל מ:</Typography>
        {expanded ? <ExpandLessIcon sx={{ fontSize: 15, color: '#155724' }} /> : <ExpandMoreIcon sx={{ fontSize: 15, color: '#155724' }} />}
      </Stack>
      <Collapse in={expanded} timeout={150} unmountOnExit>
        <Stack spacing={0.5} mt={0.6}>
          {groups.map((g, i) => {
            const key = `${g.source_type}:${g.supplier_id ?? 'null'}`;
            const isOpen = openBatches === key;
            const prodBatchIds = g.source_type === 'production'
              ? [...new Set(g.batches.map(b => b.production_batch_id).filter(Boolean))]
              : [];
            return (
              <Box key={i}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Chip size="small" label={sourceRowLabel(g)}
                    sx={{ height: 20, fontSize: 10, fontWeight: 700, backgroundColor: '#D4EDDA', color: '#155724' }} />
                  <Typography fontSize={12} fontWeight={600}>{fmtQty(g.net_qty, unit)}</Typography>
                  {g.returned_qty > 0 && (
                    <Typography fontSize={10} color="text.secondary">
                      (מתוך {fmtQty(g.fulfilled_qty, unit)} — {fmtQty(g.returned_qty, unit)} הוחזרו)
                    </Typography>
                  )}
                  {prodBatchIds.map(id => (
                    <Button key={id} size="small" onClick={() => navigate(`/production_batches/${id}/show`)}
                      sx={{ fontSize: 10, minWidth: 0, px: 0.5, textDecoration: 'underline', color: '#0d1d3e' }}>
                      מנת ייצור #{id}
                    </Button>
                  ))}
                  {g.batches.length > 1 && (
                    <IconButton size="small" onClick={() => setOpenBatches(isOpen ? null : key)} aria-expanded={isOpen} aria-label={isOpen ? 'הסתר מנות ייצור נוספות' : 'הצג מנות ייצור נוספות'} sx={{ p: 0.2 }}>
                      {isOpen ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  )}
                </Stack>
                {g.batches.length > 1 && (
                  <Collapse in={isOpen} timeout={150} unmountOnExit>
                    <Stack spacing={0.2} pr={2} pt={0.3}>
                      {g.batches.map((b, bi) => (
                        <Typography key={bi} fontSize={10} color="text.secondary">
                          אצווה מ-{fmtDate(b.batch_created_at)} — {fmtQty(b.net_qty, unit)}
                          {b.returned_qty > 0 ? ` (מתוך ${fmtQty(b.qty, unit)})` : ''}
                        </Typography>
                      ))}
                    </Stack>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default SupplierFulfillmentPanel;
